import { Box, Stack } from "@mui/material";
import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useAppDispatch } from "@/app/store";
import { useAuth } from "@/shared/auth/useAuth";
import { useCognitoSignOut } from "@/shared/auth/useCognitoSignOut";
import { usePageLayout } from "@/app/routes/pageLayout";
import { LAYOUT_AVATAR_Z_INDEX, PAGE_PADDING, PERMISSION } from "@/constants";
import { fetchTenantFeatures } from "@/features/app-config/model/tenantFeaturesSlice";
import { useBrandDisplay } from "@/features/settings/hooks/useBranding";
import { fetchBranding } from "@/features/settings/model/brandingSlice";
import { useFavoritePublishedAppsPrefetch } from "@/features/published-apps/hooks/useFavoritePublishedAppsPrefetch";
import { usePermissions } from "@/features/users/hooks/usePermissions";
import { fetchCurrentUser } from "@/features/users/model/usersSlice";
import { useFavicon } from "@/shared/hooks/useFavicon";
import { AppSidebar } from "@/shared/ui/layout/AppSidebar";
import { PageContainer } from "@/shared/ui/layout/PageContainer";
import { SecondarySidebar } from "@/shared/ui/layout/SecondarySidebar";
import { SecondarySidebarProvider } from "@/shared/ui/layout/SecondarySidebarProvider";
import { SidebarCollapseProvider } from "@/shared/ui/layout/SidebarCollapseProvider";
import { UserAccountMenu } from "@/shared/ui/layout/UserAccountMenu";

export function MainLayout() {
  const { isAuthenticated } = useAuth();
  const dispatch = useAppDispatch();
  const signOut = useCognitoSignOut();
  const { maxWidthPx, secondarySidebar } = usePageLayout();
  const { has } = usePermissions();
  const { iconSrc } = useBrandDisplay();

  // The browser tab shows the icon alone -- the same image as the collapsed sidebar, no name.
  useFavicon(iconSrc);

  // Signing in is where the viewer's favourites become known, so it is also where they are warmed:
  // by the time one is clicked on the home page, its data is already in memory. Gated on the same
  // permission the home page gates its favourites widget on, so a viewer who cannot see published
  // apps never issues a request that would only come back 403.
  useFavoritePublishedAppsPrefetch({
    enabled: isAuthenticated && has(PERMISSION.PUBLISHED_APP_VIEW),
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    void dispatch(fetchCurrentUser());
    // Tenant entitlements ride alongside the current user rather than in the pre-login
    // configuration gate: the endpoint is authenticated, and nothing here may block signing in.
    // This layout is the tenant app only, so the admin host never issues this request.
    void dispatch(fetchTenantFeatures());
    // Every signed-in user reads the branding: it is what the sidebar and the tab icon show.
    void dispatch(fetchBranding());
  }, [isAuthenticated, dispatch]);

  return (
    <SidebarCollapseProvider>
    <SecondarySidebarProvider enabled={secondarySidebar}>
      <Box sx={{ display: "flex", minHeight: "100%", height: "100%" }}>
        <AppSidebar />
        <Box
          component="main"
          sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: maxWidthPx,
                mx: "auto",
                minHeight: "100%",
                height: secondarySidebar ? "100%" : "auto",
                display: "flex",
                alignItems: "stretch",
                position: "relative",
              }}
            >
              {isAuthenticated ? (
                <Box
                  sx={{
                    position: "absolute",
                    top: PAGE_PADDING.DEFAULT.Y_PX,
                    right: PAGE_PADDING.DEFAULT.RIGHT_PX,
                    zIndex: LAYOUT_AVATAR_Z_INDEX,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <UserAccountMenu onLogout={signOut} />
                  </Stack>
                </Box>
              ) : null}
              <SecondarySidebar enabled={secondarySidebar} />
              <PageContainer>
                <Outlet />
              </PageContainer>
            </Box>
          </Box>
        </Box>
      </Box>
    </SecondarySidebarProvider>
    </SidebarCollapseProvider>
  );
}
